<template>
  <ListPageShell>
    <template #header>
      <div class="list-page-panel is-header-minimal px-4 py-3 md:px-6">
        <div>
          <h1 class="list-page-title">电子签回调日志</h1>
          <p class="mt-2 text-sm leading-6 text-slate-600">查看腾讯电子签回调原文、处理结果和失败重放记录，用于排查签署状态同步问题。</p>
        </div>
      </div>
    </template>

    <template #filters>
      <PageFilterBar :show-refresh="true" :refresh-loading="loading" @refresh="fetchList">
        <input v-model="filters.eventId" class="form-input form-control-sm filter-bar-field-sm" placeholder="事件 ID" />
        <input v-model="filters.eventType" class="form-input form-control-sm filter-bar-field-sm" placeholder="事件类型" />
        <input v-model="filters.tencentFlowId" class="form-input form-control-sm filter-bar-field" placeholder="腾讯 FlowId" />
        <select v-model="filters.processStatus" class="form-select-native form-control-sm filter-bar-field-sm">
          <option value="">全部状态</option>
          <option value="PENDING">待处理</option>
          <option value="SUCCESS">成功</option>
          <option value="FAILED">失败</option>
          <option value="IGNORED">已忽略</option>
        </select>
        <template #searchActions>
          <button class="btn-primary" type="button" @click="handleSearch">搜索</button>
          <button class="btn-secondary" type="button" @click="handleReset">重置</button>
        </template>
      </PageFilterBar>
    </template>

    <div class="space-y-4">
      <div class="grid gap-3 md:grid-cols-4">
        <div v-for="item in summaryCards" :key="item.label" class="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
          <p class="text-xs font-medium text-slate-500">{{ item.label }}</p>
          <p class="mt-2 text-2xl font-semibold text-slate-900">{{ item.value }}</p>
        </div>
      </div>

      <div class="rounded-[24px] border border-slate-200 bg-white p-4 shadow-[0_18px_50px_rgba(15,23,42,0.06)]">
        <div class="data-table-shell border-none shadow-none">
          <div class="data-table-scroll">
            <table class="data-table text-sm">
              <thead class="data-table-head">
                <tr>
                  <th class="data-table-th text-left">事件</th>
                  <th class="data-table-th text-left">腾讯 FlowId</th>
                  <th class="data-table-th text-left">处理状态</th>
                  <th class="data-table-th text-left">处理信息</th>
                  <th class="data-table-th text-left">接收时间</th>
                  <th class="data-table-th text-left">处理时间</th>
                  <th class="data-table-th is-center">操作</th>
                </tr>
              </thead>
              <tbody>
                <tr v-if="loading" class="data-table-row"><td colspan="7" class="data-table-empty">正在加载回调日志...</td></tr>
                <tr v-else-if="!list.length" class="data-table-row"><td colspan="7" class="data-table-empty">暂无回调日志</td></tr>
                <tr v-for="item in list" v-else :key="item.id" class="data-table-row">
                  <td class="data-table-td min-w-[220px]">
                    <div class="data-table-cell-title">{{ item.eventType || '-' }}</div>
                    <div class="data-table-cell-meta">{{ item.eventId || item.id }}</div>
                  </td>
                  <td class="data-table-td font-mono text-xs text-slate-600">{{ blank(item.tencentFlowId) }}</td>
                  <td class="data-table-td"><span :class="['status-badge', statusClassByResult(item.processStatus)]">{{ processStatusText(item.processStatus) }}</span></td>
                  <td class="data-table-td max-w-[260px] truncate text-slate-600" :title="item.processMessage">{{ blank(item.processMessage) }}</td>
                  <td class="data-table-td whitespace-nowrap text-slate-600">{{ formatDateTime(item.receivedTime) || '-' }}</td>
                  <td class="data-table-td whitespace-nowrap text-slate-600">{{ formatDateTime(item.processedTime) || '-' }}</td>
                  <td class="data-table-td is-center whitespace-nowrap">
                    <div class="flex items-center justify-center gap-1">
                      <button class="table-action-button is-primary" @click="openDetail(item)">详情</button>
                      <button v-if="item.processStatus === 'FAILED'" class="table-action-button is-danger" @click="retryLog(item)">重放</button>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
        <AppPagination class="pagination-bridge" :pagination="pagination" :page-sizes="[10, 20, 50]" :show-page-count="true" @page-change="handlePageChange" @size-change="handlePageSizeChange" />
      </div>
    </div>

    <el-drawer v-model="detailVisible" title="回调详情" size="760px">
      <div v-if="detail" class="space-y-5">
        <section class="rounded-2xl border border-slate-200 p-4">
          <h3 class="text-sm font-semibold text-slate-900">基础信息</h3>
          <dl class="mt-4 grid grid-cols-2 gap-3 text-sm">
            <div><dt class="text-slate-500">事件 ID</dt><dd class="mt-1 text-slate-900">{{ detail.eventId || detail.id }}</dd></div>
            <div><dt class="text-slate-500">事件类型</dt><dd class="mt-1 text-slate-900">{{ detail.eventType || '-' }}</dd></div>
            <div><dt class="text-slate-500">腾讯 FlowId</dt><dd class="mt-1 font-mono text-xs">{{ blank(detail.tencentFlowId) }}</dd></div>
            <div><dt class="text-slate-500">处理状态</dt><dd class="mt-1">{{ processStatusText(detail.processStatus) }}</dd></div>
          </dl>
        </section>
        <section class="rounded-2xl border border-slate-200 p-4">
          <h3 class="text-sm font-semibold text-slate-900">处理信息</h3>
          <p class="mt-3 text-sm text-slate-600">{{ blank(detail.processMessage) }}</p>
        </section>
        <section class="rounded-2xl border border-slate-200 p-4">
          <h3 class="text-sm font-semibold text-slate-900">Raw Headers</h3>
          <pre class="mt-3 max-h-64 overflow-auto rounded-xl bg-slate-950 p-4 text-xs leading-5 text-slate-100">{{ safeJson(detail.rawHeaders) || '-' }}</pre>
        </section>
        <section class="rounded-2xl border border-slate-200 p-4">
          <h3 class="text-sm font-semibold text-slate-900">Raw Body</h3>
          <pre class="mt-3 max-h-80 overflow-auto rounded-xl bg-slate-950 p-4 text-xs leading-5 text-slate-100">{{ safeJson(detail.rawBody) || '-' }}</pre>
        </section>
      </div>
    </el-drawer>
  </ListPageShell>
</template>

<script setup>
import { computed, onMounted, reactive, ref } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import AppPagination from '@/components/AppPagination.vue'
import ListPageShell from '@/components/ListPageShell.vue'
import PageFilterBar from '@/components/PageFilterBar.vue'
import { getEsignCallbackLogPage, retryEsignCallbackLog } from '@/api/esign'
import { blank, formatDateTime, normalizePage, processStatusText, safeJson, statusClassByResult } from './esign-utils'

const loading = ref(false)
const list = ref([])
const detail = ref(null)
const detailVisible = ref(false)
const filters = reactive({ eventId: '', eventType: '', tencentFlowId: '', processStatus: '' })
const pagination = ref({ current: 1, size: 10, total: 0, pages: 1 })

const summaryCards = computed(() => [
  { label: '当前页日志', value: list.value.length },
  { label: '成功', value: list.value.filter(item => item.processStatus === 'SUCCESS').length },
  { label: '失败', value: list.value.filter(item => item.processStatus === 'FAILED').length },
  { label: '待处理', value: list.value.filter(item => item.processStatus === 'PENDING').length }
])

const fetchList = async () => {
  loading.value = true
  try {
    const res = await getEsignCallbackLogPage({ current: pagination.value.current, size: pagination.value.size, ...filters })
    const page = normalizePage(res, pagination.value)
    list.value = page.records
    pagination.value = page.pagination
  } catch (error) {
    ElMessage.error(error.message || '加载回调日志失败')
  } finally {
    loading.value = false
  }
}

const handleSearch = () => {
  pagination.value.current = 1
  fetchList()
}

const handleReset = () => {
  Object.assign(filters, { eventId: '', eventType: '', tencentFlowId: '', processStatus: '' })
  handleSearch()
}

const handlePageChange = page => {
  pagination.value.current = page
  fetchList()
}

const handlePageSizeChange = size => {
  pagination.value.size = size
  pagination.value.current = 1
  fetchList()
}

const openDetail = item => {
  detail.value = item
  detailVisible.value = true
}

const retryLog = async (item) => {
  await ElMessageBox.confirm('确认重放这条回调日志吗？重放会再次执行回调处理逻辑。', '重放回调', {
    type: 'warning',
    confirmButtonText: '确认重放',
    cancelButtonText: '取消'
  })
  try {
    await retryEsignCallbackLog(item.id)
    ElMessage.success('回调重放已提交')
    fetchList()
  } catch (error) {
    ElMessage.error(error.message || '回调重放失败')
  }
}

onMounted(fetchList)
</script>
